import React, { useEffect, useState } from "react";
import { Button, Input } from "reactstrap";

import { getRowSizeValue } from "../wizardCreateHelpers";
import WizardIcon from "./WizardIcon";

const PhotoTile = ({ t, item, colorLabel, isFirst, isLast, isMenuOpen, onToggleMenu, onAction }) => (
  <div className={`catalog-wizard-photo${item.isColorCover ? " is-color-cover" : ""}`}>
    {item.sourceUrl ? <img src={item.sourceUrl} alt={item.originalName || colorLabel || ""} /> : null}
    <div className="catalog-wizard-photo__badges">
      {colorLabel && item.isColorCover ? (
        <span className="catalog-wizard-photo__badge">{t("catalogProductStudio.step3CoverBadge")}</span>
      ) : null}
      {item.isCover ? (
        <span className="catalog-wizard-photo__badge catalog-wizard-photo__badge--main">
          {t("catalogProductStudio.step3ProductCoverBadge")}
        </span>
      ) : null}
    </div>
    <button
      type="button"
      className="catalog-wizard-photo__menu-toggle"
      aria-label={t("catalogProductStudio.step3PhotoMenu")}
      aria-expanded={isMenuOpen}
      onClick={onToggleMenu}
    >
      <WizardIcon name="more" size={16} />
    </button>
    {isMenuOpen ? (
      <div className="catalog-wizard-photo__menu" role="menu">
        {colorLabel ? (
          <button type="button" role="menuitem" disabled={item.isColorCover} onClick={() => onAction("colorCover")}>
            {t("catalogProductStudio.step3MenuSetColorCover")}
          </button>
        ) : null}
        <button type="button" role="menuitem" disabled={item.isCover} onClick={() => onAction("productCover")}>
          {t("catalogProductStudio.step3MenuSetProductCover")}
        </button>
        <button type="button" role="menuitem" disabled={isFirst} onClick={() => onAction("left")}>
          {t("catalogProductStudio.step3MenuMoveLeft")}
        </button>
        <button type="button" role="menuitem" disabled={isLast} onClick={() => onAction("right")}>
          {t("catalogProductStudio.step3MenuMoveRight")}
        </button>
        <button type="button" role="menuitem" className="is-danger" onClick={() => onAction("remove")}>
          {t("catalogProductStudio.step3MenuRemove")}
        </button>
      </div>
    ) : null}
  </div>
);

const Step3PhotosStock = ({
  t,
  colorGroups,
  mediaItems,
  quantityByRowKey,
  onUploadForColor,
  onRemoveMediaItem,
  onSetProductCover,
  onSetColorCover,
  onMoveMediaItem,
  onQuantityChange,
  onSameStockForColor,
  onClearStockForColor,
  onSetPrimaryColor,
  onRemoveColorGroup,
  isUploadingMediaAsset,
  locations,
  locationId,
  onLocationChange,
  variantCount,
  matrixText,
  getSwatch,
}) => {
  const [showHelp, setShowHelp] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [sameStockDraft, setSameStockDraft] = useState({});

  useEffect(() => {
    if (!openMenuId) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!event.target.closest?.(".catalog-wizard-photo")) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [openMenuId]);

  const productCoverColor = mediaItems.find((item) => item.isCover)?.colorLabel ?? null;
  const isComplete = colorGroups.every((group) => {
    const hasPhoto = mediaItems.some((item) =>
      group.color ? item.colorLabel === group.color : !item.colorLabel
    );
    const hasStock = group.rows.every((row) => Number(quantityByRowKey[row.key] || 0) > 0);
    return hasPhoto && hasStock;
  });

  const handlePhotoAction = (item, colorLabel, action) => {
    setOpenMenuId(null);
    if (action === "colorCover") onSetColorCover(item.id, colorLabel);
    if (action === "productCover") onSetProductCover(item.id);
    if (action === "left") onMoveMediaItem(item.id, -1);
    if (action === "right") onMoveMediaItem(item.id, 1);
    if (action === "remove") onRemoveMediaItem(item.id);
  };

  return (
    <div>
      <div className="catalog-wizard-panel__head">
        <div>
          <h2 className="catalog-wizard-panel__title">{t("catalogProductStudio.step3Title")}</h2>
          <p className="catalog-wizard-panel__copy">{t("catalogProductStudio.step3Copy")}</p>
        </div>
        <button
          type="button"
          className="catalog-wizard-chip-button"
          aria-expanded={showHelp}
          onClick={() => setShowHelp((current) => !current)}
        >
          <WizardIcon name="info" size={18} className="catalog-wizard-info__svg" />
          {t("catalogProductStudio.wizardHowItWorks")}
        </button>
      </div>

      {showHelp ? (
        <div className="catalog-wizard-info mb-3" style={{ maxWidth: "none" }}>
          <WizardIcon name="info" size={20} className="catalog-wizard-info__svg" />
          <div>{t("catalogProductStudio.step3HowCopy")}</div>
        </div>
      ) : null}

      {locations.length > 1 ? (
        <div className="catalog-wizard-field catalog-wizard-location">
          <label htmlFor="wizard-location">{t("catalogProductStudio.step3StockLocation")}</label>
          <Input
            id="wizard-location"
            type="select"
            value={locationId}
            onChange={(event) => onLocationChange(event.target.value)}
          >
            <option value="">{t("catalogProductStudio.createChooseLocation")}</option>
            {locations.map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </Input>
        </div>
      ) : null}

      {colorGroups.map((group) => {
        const groupKey = group.color || "__default__";
        const inputId = `wizard-upload-${groupKey.replace(/[^a-z0-9]+/gi, "-")}`;
        const groupItems = mediaItems.filter((item) =>
          group.color ? item.colorLabel === group.color : !item.colorLabel
        );
        const draftValue = sameStockDraft[groupKey] ?? "";
        const hasSizes = group.rows.some((row) => getRowSizeValue(row) !== null);

        return (
          <section key={groupKey} className="catalog-wizard-color-section" data-testid={`wizard-color-${groupKey}`}>
            <div className="catalog-wizard-color-section__head">
              {group.color ? (
                <span
                  className="catalog-wizard-swatch catalog-wizard-swatch--xl"
                  style={{ "--catalog-color-swatch-value": getSwatch(group.color) }}
                  aria-hidden="true"
                />
              ) : null}
              <div className="catalog-wizard-color-section__name">
                <strong>{group.color || t("catalogProductStudio.step3NoColorSectionTitle")}</strong>
                <span>
                  {group.rows.length === 1
                    ? t("catalogProductStudio.variantsCountOne")
                    : t("catalogProductStudio.variantsCount").replace("{count}", String(group.rows.length))}
                  {" · "}
                  {groupItems.length
                    ? t("catalogProductStudio.step3PhotoCount").replace("{count}", String(groupItems.length))
                    : t("catalogProductStudio.step3UploadHint")}
                </span>
              </div>
              {group.color ? (
                <div className="catalog-wizard-color-section__actions">
                  <button
                    type="button"
                    className={`catalog-wizard-chip-button${productCoverColor === group.color ? " is-active" : ""}`}
                    disabled={!groupItems.length}
                    onClick={() => onSetPrimaryColor(group.color)}
                  >
                    <WizardIcon name="star" size={15} />
                    {productCoverColor === group.color
                      ? t("catalogProductStudio.step3PrimaryColor")
                      : t("catalogProductStudio.step3SetPrimaryColor")}
                  </button>
                  <button
                    type="button"
                    className="catalog-wizard-chip-button catalog-wizard-chip-button--danger"
                    onClick={() => onRemoveColorGroup(group.color, groupItems.length)}
                  >
                    <WizardIcon name="trash" size={15} />
                    {t("catalogProductStudio.step3RemoveColor")}
                  </button>
                </div>
              ) : null}
            </div>

            <input
              id={inputId}
              className="catalog-wizard-upload-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              disabled={isUploadingMediaAsset}
              onChange={(event) => {
                onUploadForColor(group.color, event.target.files);
                event.target.value = "";
              }}
            />
            <div className="catalog-wizard-photos">
              <label htmlFor={inputId} className="catalog-wizard-upload-tile">
                <WizardIcon name="upload" size={22} className="catalog-wizard-upload-tile__plus" />
                <strong>
                  {isUploadingMediaAsset
                    ? t("catalogProductStudio.mediaUploading")
                    : t("catalogProductStudio.step3UploadPhoto")}
                </strong>
                <span>{t("catalogProductStudio.step3UploadFormats")}</span>
                <span>{t("catalogProductStudio.step3UploadMax")}</span>
              </label>
              {groupItems.map((item, index) => (
                <PhotoTile
                  key={item.id}
                  t={t}
                  item={item}
                  colorLabel={group.color}
                  isFirst={index === 0}
                  isLast={index === groupItems.length - 1}
                  isMenuOpen={openMenuId === item.id}
                  onToggleMenu={() => setOpenMenuId((current) => (current === item.id ? null : item.id))}
                  onAction={(action) => handlePhotoAction(item, group.color, action)}
                />
              ))}
              {groupItems.length ? (
                <label htmlFor={inputId} className="catalog-wizard-upload-tile">
                  <WizardIcon name="plus" size={22} className="catalog-wizard-upload-tile__plus" />
                  <span>{t("catalogProductStudio.step3AddMorePhotos")}</span>
                </label>
              ) : null}
            </div>

            <div className="catalog-wizard-stock">
              <div>
                <div className="catalog-wizard-label">
                  {hasSizes
                    ? t("catalogProductStudio.step3StockPerSize")
                    : t("catalogProductStudio.step3StockSingle")}
                </div>
                <div className="catalog-wizard-stock__inputs">
                  {group.rows.map((row) => {
                    const sizeLabel = getRowSizeValue(row) ?? t("catalogProductStudio.step3StockSingle");
                    const stockInputId = `wizard-stock-${row.key}`;
                    return (
                      <div key={row.key} className="catalog-wizard-stock__cell">
                        <label htmlFor={stockInputId} title={row.label}>
                          {sizeLabel}
                        </label>
                        <Input
                          id={stockInputId}
                          type="number"
                          min="0"
                          inputMode="numeric"
                          aria-label={`${row.label}`}
                          value={quantityByRowKey[row.key] ?? ""}
                          placeholder="0"
                          onChange={(event) => onQuantityChange(row.key, event.target.value)}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {group.rows.length > 1 ? (
                <div className="catalog-wizard-stock__all">
                  <span className="catalog-wizard-stock__all-label">
                    {t("catalogProductStudio.step3SameStockAll")}
                  </span>
                  <Input
                    type="number"
                    min="0"
                    inputMode="numeric"
                    aria-label={t("catalogProductStudio.step3SameStockAll")}
                    placeholder="0"
                    value={draftValue}
                    onChange={(event) =>
                      setSameStockDraft((current) => ({ ...current, [groupKey]: event.target.value }))
                    }
                  />
                  <Button color="secondary" onClick={() => onSameStockForColor(group.rows, draftValue)}>
                    {t("catalogProductStudio.step3Apply")}
                  </Button>
                  <button
                    type="button"
                    className="catalog-wizard-text-danger"
                    onClick={() => onClearStockForColor(group.rows)}
                  >
                    <WizardIcon name="trash" size={15} />
                    {t("catalogProductStudio.step3ClearStock")}
                  </button>
                </div>
              ) : null}
            </div>
          </section>
        );
      })}

      <div className="catalog-wizard-bottom-tiles">
        <div className="catalog-wizard-tile">
          <WizardIcon name="cube" size={26} className="catalog-wizard-tile__icon" />
          <div>
            <span className="catalog-wizard-tile__title">
              {variantCount === 1
                ? t("catalogProductStudio.variantsCountOne")
                : t("catalogProductStudio.step3TotalVariants").replace("{count}", String(variantCount))}
            </span>
            <span className="catalog-wizard-tile__copy">{matrixText}</span>
          </div>
        </div>
        <div className={`catalog-wizard-tile${isComplete ? " catalog-wizard-tile--ok" : ""}`}>
          <span
            className={`catalog-wizard-status-icon${isComplete ? "" : " catalog-wizard-status-icon--warn"}`}
            aria-hidden="true"
          >
            {isComplete ? <WizardIcon name="check" size={14} strokeWidth={3} /> : "!"}
          </span>
          <div>
            <span className="catalog-wizard-tile__title">
              {isComplete
                ? t("catalogProductStudio.step3AllCompleteTitle")
                : t("catalogProductStudio.step3IncompleteTitle")}
            </span>
            <span className="catalog-wizard-tile__copy">{t("catalogProductStudio.step3IncompleteCopy")}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step3PhotosStock;
